import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Formik } from 'formik';
import { createContext, useCallback, useContext, useMemo, useState, } from 'react';
import * as Yup from 'yup';
import { useChartData } from '../../context/ChartContext';
import { useDispatch } from '../../context/DispatchContext';
import { useScaleChecked } from '../../context/ScaleContext';
import FormikInput from '../../elements/formik/FormikInput';
import { useActiveSpectrum } from '../../hooks/useActiveSpectrum';
const validationSchema = Yup.object({
    value: Yup.number().required(),
});
const InputDimension = { height: 28, width: 100 };
const peaksEditionContext = {
    onEdit: () => {
        // Empty default.
    },
};
const PeaksEditionContext = createContext(peaksEditionContext);
function usePeaksEditionManager() {
    const context = useContext(PeaksEditionContext);
    if (!context) {
        throw new Error('Peak edition manager context was not found');
    }
    return context;
}
function stopPropagation(e) {
    e.stopPropagation();
}
export function PeakEditionProvider({ children }) {
    const { scaleX, scaleY } = useScaleChecked();
    const { width } = useChartData();
    const spectrum = useActiveSpectrum();
    const [peak, setPeak] = useState(null);
    function getPosition() {
        let x = 0;
        let y = 0;
        if (!peak) {
            return { x, y };
        }
        const { x: px, y: py, dx, dy, useScaleX, useScaleY } = peak;
        x = px + dx;
        if (useScaleX) {
            x = scaleX()(px) + dx;
        }
        y = py + dy;
        if (useScaleY) {
            y = scaleY(spectrum?.id)(py) + dy;
        }
        if (x + InputDimension.width > width) {
            x = x - InputDimension.width;
        }
        return { x, y };
    }
    const handleOnEdit = useCallback((e, peak) => {
        e.stopPropagation();
        if (peak) {
            setPeak({ ...peak });
        }
        else {
            setPeak(null);
        }
    }, []);
    const editionManagerState = useMemo(() => ({ onEdit: handleOnEdit, id: peak?.id }), [peak?.id, handleOnEdit]);
    const { x, y } = getPosition();
    return (_jsx(PeaksEditionContext.Provider, { value: editionManagerState, children: _jsxs("div", { onClick: () => setPeak(null), onContextMenu: () => setPeak(null), style: { position: 'relative' }, children: [peak && (_jsx("div", { style: {
                        position: 'absolute',
                        display: 'block',
                        transform: `translate(${x}px,${y}px)`,
                    }, children: _jsx(PeakEditionField, { value: peak.value, onClose: () => setPeak(null) }, peak.id) })), children] }) }));
}
function PeakEditionField({ value, onClose }) {
    const dispatch = useDispatch();
    function keyDownCheck(event) {
        if (event.key === 'Enter') {
            return true;
        }
        else if (event.key === 'Escape') {
            onClose();
            return false;
        }
    }
    function hanldeOnSubmit({ value: newValue }) {
        if (value) {
            const shift = newValue - value;
            dispatch({ type: 'SHIFT_SPECTRUM', payload: { shift } });
            onClose();
        }
    }
    return (_jsx(Formik, { initialValues: { value }, onSubmit: hanldeOnSubmit, validationSchema: validationSchema, children: ({ submitForm }) => (_jsx(FormikInput, { type: "number", style: {
                input: {
                    height: `${InputDimension.height}px`,
                    padding: '5px',
                    outline: 'none',
                },
            }, name: "value", autoSelect: true, onKeyDown: (e) => keyDownCheck(e) && submitForm(), onClick: stopPropagation, onMouseDown: stopPropagation })) }));
}
export function PeakEditionListener(props) {
    const { children, x, y, value, useScaleX = false, useScaleY = false, dx = 0, dy = -InputDimension.height, id, } = props;
    const { onEdit, id: editingPeakId } = usePeaksEditionManager();
    return (_jsx("g", { onClick: (e) => onEdit(e, { x, y, value, useScaleX, useScaleY, dx, dy, id }), children: editingPeakId !== id && children }));
}
